import * as React from "react";
import { firebaseDB, firebaseApp } from "../../firebase";
import { StaticData, Quad } from "../../types";
import ErrorBoundary from "../../components/ErrorBoundary";
import {
	Button,
	Menu,
	Container,
	Divider,
	Accordion,
	Input,
	AccordionTitle,
	AccordionContent,
	Header
} from "semantic-ui-react";
import Markdown from "../../components/WrappedMarkdown";
import MarkdownEditor from "react-mde";
import "react-mde/lib/styles/css/react-mde-all.css";
import { AdminEditorSection } from "./AdminEditorSection";
import { Prompt } from "react-router";
import { AuthContext } from "../../helpers/WithLoggedIn";

type Quadrants = { [id: string]: Quad };
type StaticDataObj = { [id: string]: StaticData };

interface IState {
	staticdata: { [id: string]: StaticData };
	quadrants: { [id: string]: Quad };

	plainTextEditor: string;
	mdEditor: string;
	selectedKey: string;
	componentName: string;
	hasChanged: boolean;
	didError: boolean;
}

const EditingSection = ({
	mdEditor,
	handleChange,
	plainTextValue,
	handlePlainTextChange,
	updateComponentName,
	componentName
}) => {
	const [activeIndex, setIndex] = React.useState(-1);
	const handleClick = React.useCallback(
		(e, titleProps) => {
			const { index } = titleProps;
			setIndex(activeIndex == index ? -1 : index);
		},
		[setIndex, activeIndex]
	);

	const handleInputChange = React.useCallback(
		evt => {
			updateComponentName(evt.currentTarget.value);
		},
		[updateComponentName]
	);

	return (
		<>
			<AdminEditorSection
				index={2}
				active={activeIndex === 2}
				onClick={handleClick}
				title="Component Name"
				description=""
			>
				<Input
					type="text"
					value={componentName}
					onChange={handleInputChange}
				/>
			</AdminEditorSection>
			<AdminEditorSection
				active={activeIndex === 0}
				onClick={handleClick}
				index={0}
				title="Markdown"
				description="This text will be displayed on the website"
			>
				<MarkdownEditor
					// style={{ width: 500, minHeight: 300 }}
					value={mdEditor}
					onChange={handleChange}
					minEditorHeight={500}
					maxEditorHeight={500}
					minPreviewHeight={0}
					generateMarkdownPreview={markdown =>
						Promise.resolve("null")
					}
				/>
			</AdminEditorSection>

			<AdminEditorSection
				index={1}
				onClick={handleClick}
				active={activeIndex === 1}
				description="This text will be displayed in the PDF document"
				title="Plaintext"
			>
				<textarea
					style={{ width: 500, minHeight: 300 }}
					value={plainTextValue}
					onChange={handlePlainTextChange}
				/>
			</AdminEditorSection>
		</>
	);
};

interface QuestionSectionProps {
	quadrants: Quadrants;
	staticdata: StaticDataObj;
	selectedKey: string;
	onSelect: (key: string) => void;
}

const UnauthorisedMessage = () => { 
	const context = React.useContext(AuthContext);
	return (
		<div style={{flexGrow: 1, display: 'flex', flexDirection: 'row'}}>
		<div style={{margin: '0 auto', padding: 24}}>	
			<Header>Something went wrong</Header>
			<p>You may not have permission to do this</p>
			<p>Your user id is: {context.userId}</p>
		</div>
	</div>
	)
}

const QuestionSection: React.FC<QuestionSectionProps> = React.memo(
	({ quadrants, staticdata, selectedKey, onSelect }) => {
		const [activeIndex, setIndex] = React.useState("");
		const handleClick = React.useCallback(
			(e, titleProps) => {
				const { index } = titleProps;
				setIndex(activeIndex == index ? -1 : index);
			},
			[setIndex, activeIndex]
		);

		if (quadrants == undefined) return null;
		return (
			<Menu vertical>
				{Object.keys(staticdata).map(key => (
					<Accordion>
						<AccordionTitle
							index={key}
							onClick={handleClick}
							active={
								key === activeIndex ||
								staticdata[key].quadrantOrder.indexOf(
									selectedKey
								) > -1
							}
						>
							{key}
						</AccordionTitle>
						<AccordionContent
							index={key}
							active={
								key === activeIndex ||
								staticdata[key].quadrantOrder.indexOf(
									selectedKey
								) > -1
							}
						>
							{staticdata[key].quadrantOrder.map(quadId => {
								if (
									typeof quadId == "string" &&
									quadrants[quadId]
								) {
									return (
										<Menu.Item
											active={selectedKey == quadId}
											onClick={() => onSelect(quadId)}
										>
											{quadrants[quadId].title}
										</Menu.Item>
									);
								}
							})}
						</AccordionContent>
					</Accordion>
					// <Menu.Item>{key}</Menu.Item>
				))}
			</Menu>
		);
	}
);

const CATH_USER_ID = 'ra5DWE5WicfRYLVRI1ZrvtE9mBw1', SAM_USER_ID = '2XayBMuglPOvT16hRKrehvFBe0v2';

class AdminEditor extends React.Component<{}, IState> {
	state = {
		staticdata: {},
		quadrants: {},
		plainTextEditor: "",
		mdEditor: "",
		selectedKey: "",
		componentName: "",
		hasChanged: false,
		didError: false
	};

	componentDidMount() {
		//Register Listeners
		firebaseDB
			.ref("/staticdata")
			.on("value", data => this.setState({ staticdata: data.val() }));
		firebaseDB
			.ref("/quadrants")
			.on("value", data => this.setState({ quadrants: data.val() }));


		firebaseApp.auth().onAuthStateChanged( (auth) => { 
			if (auth != null && 
				auth.uid !== CATH_USER_ID ||
				auth.uid !== SAM_USER_ID
			) {
				this.setState({ didError: true })
			}
		})
	}

	selectQuestion = key => {
		this.setState({
			mdEditor: this.state.quadrants[key].richQuestion || "",
			plainTextEditor: this.state.quadrants[key].question || "",
			selectedKey: key,
			componentName: this.state.quadrants[key].title || "",
			hasChanged: false
		});
	};

	renderPreview() {
		return (
			<ErrorBoundary>
				<div style={{ display: "flex", flexDirection: "column" }}>
					<h1> Markdown </h1>
					<Markdown source={this.state.mdEditor} />
					<Divider />
					<h1>Teaser Box </h1>
					<p>{this.state.plainTextEditor}</p>
				</div>
			</ErrorBoundary>
		);
	}

	handleChange = val => {
		this.setState({ mdEditor: val, hasChanged: true });
	};

	handleNameChange = (newName: string) => {
		this.setState({ componentName: newName, hasChanged: true });
	};

	handleSave = () => {
		firebaseDB.ref("/quadrants/" + this.state.selectedKey).update({
			question: this.state.plainTextEditor,
			richQuestion: this.state.mdEditor,
			title: this.state.componentName
		}).catch( err => { 
			if(err.code === "PERMISSION_DENIED") { 
				this.setState({ didError: true });
			} else { 
				throw err; 
			}
		});
		this.setState({
			hasChanged: false
		});
	};

	render() {
		if(this.state.didError) { 
			return (
				<UnauthorisedMessage/>
			)
		}
		return (
			<div style={{ flexGrow: 1, display: "flex", flexDirection: "row" }}>
				<Prompt
					when={this.state.hasChanged}
					message={
						"Are you sure you wish to leave? You have unsaved changes"
					}
				/>
				<div style={{ maxWidth: 300 }}>
					<QuestionSection
						quadrants={this.state.quadrants}
						staticdata={this.state.staticdata}
						onSelect={this.selectQuestion}
						selectedKey={this.state.selectedKey}
					/>
				</div>

				<div
					style={{
						margin: "0 48px",
						width: 500,
						display: "flex",
						flexDirection: "column"
					}}
				>
					<EditingSection
						componentName={this.state.componentName}
						updateComponentName={this.handleNameChange}
						handleChange={this.handleChange}
						mdEditor={this.state.mdEditor}
						handlePlainTextChange={evt =>
							this.setState({
								plainTextEditor: evt.target.value,
								hasChanged: true
							})
						}
						plainTextValue={this.state.plainTextEditor}
					/>
					<Button
						onClick={this.handleSave}
						disabled={!this.state.hasChanged}
						primary
					>
						Save
					</Button>
				</div>
				<Container text>{this.renderPreview()}</Container>
			</div>
		);
	}
}

export default AdminEditor;
/**
 * Real Basic Editor
 */
/*

Side bar ( Name of Questions) | Editor | Preview
*/
/*
The 4Ps or Marketing Mix  =  Product, Price, Promotion and Placement. Getting the mix right means putting the right product in the right place, at the right price, at the right time
We can use Chanel perfume as an example to illustrate marketing mix: 


| Product or Service | Core Benefit Proposition | Price | Placement | Promotion | 
| ------------- |:------------|: -----|: ----|: ---- | 
| Chanel Perfum | Classy, sophiscated and exclusive | Expensive and rarely 'on sale' | Placement is through high endd stores only | Full Page ads in fashionable magazines with classy actresses 

*/



// WEBPACK FOOTER //
// ./src/pages/AdminEditor/AdminEditor.tsx